import { GuildShield } from '@/atoms/Icons/GuildShield'
import { GuildSilverIcon } from '@/atoms/Icons/GuildSilverIcon'
import { BaseIconProps } from '@/atoms/utils'
import {
  ANGEL_GUILD_BASIC_NO_ADS_MONTHLY_ADD_A_LINE_OFFER_ID,
  ANGEL_GUILD_BASIC_MONTHLY_ADD_A_LINE_OFFER_ID,
} from '@/constants/offer-ids'
import type { GuildPlan, MembershipTier } from '@/types/codegen-federation'
import { TranslateFunction } from '@/utils/translate/translate-client'
import { GuildStatus } from '@/utils/users/usersServer'

export const NO_AD_TIERS: GuildPlan[] = ['SILVER', 'GOLD']

export interface GuildTier {
  name: string
  offerId: string
}

/**
 * This has since been renamed "Basic With Ads" in the UI
 */
export const BASIC_TIER = {
  name: 'basic',
  offerId: ANGEL_GUILD_BASIC_MONTHLY_ADD_A_LINE_OFFER_ID,
}

export const BASIC_NO_ADS_TIER = {
  name: 'basic-no-ads',
  offerId: ANGEL_GUILD_BASIC_NO_ADS_MONTHLY_ADD_A_LINE_OFFER_ID,
}

export function mapTier(guildStatus: GuildStatus): GuildTier {
  return guildStatus.guildPlan && NO_AD_TIERS.includes(guildStatus.guildPlan) ? BASIC_NO_ADS_TIER : BASIC_TIER
}

/**
 * Name the membership given, from the perspective of the gifter. For gift given
 *
 * DESIGN NOTE - Note that subscription currently doesn't have its own human-readable
 * name. In the client to date, the membership name is derived from the guild plan.
 * But the subscriptions can now be gifted, which means a sub will belong to a different
 * user than the one who is logged in with a guild plan. For now, however, we
 * can get away with this algorithm because the options of gifted membership are
 * currently directly related to the gifter. There is a potential hole here too,
 * however, because what if the gifter *changes* his subscription between the time
 * of giving and the time of viewing? This will be potentially inaccurate.
 *
 * see https://angelstudiosteam.slack.com/archives/C09P0AN0NNN/p1762285401361199?thread_ts=1762282587.113369&cid=C09P0AN0NNN
 */
export function formatGiftGivenMembershipName(
  t: TranslateFunction,
  giverGuildPlan: GuildPlan | undefined | null,
): string {
  if (!giverGuildPlan) return t('basicMonthlyWithAds', 'Basic Monthly With Ads')

  const COLOR_KEYED = {
    GOLD: t('basicMonthly', 'Basic Monthly'),
    SILVER: t('basicMonthly', 'Basic Monthly'),
    BRONZE: t('basicMonthlyWithAds', 'Basic Monthly With Ads'),
  }

  return COLOR_KEYED[giverGuildPlan]
}

export function formatGiftcardGraphicMembershipName(
  t: TranslateFunction,
  giverGuildPlan: string | GuildPlan | undefined | null,
): string {
  const DEFAULT_LABEL = t('basicWithAdsAngelMembership', 'Basic With Ads Angel Membership')
  if (!giverGuildPlan) return DEFAULT_LABEL

  const COLOR_KEYED = {
    GOLD: t('basicAngelMembership', 'Basic Angel Membership'),
    SILVER: t('basicAngelMembership', 'Basic Angel Membership'),
    BRONZE: t('basicWithAdsAngelMembership', 'Basic With Ads Angel Membership'),
  }

  const MEMBERSHIP_KEYED: Record<string, string> = {
    basic: t('basicWithAdsAngelMembership', 'Basic With Ads Angel Membership'),
    'basic-no-ads': t('basicAngelMembership', 'Basic Angel Membership'),
    BASIC: t('basicWithAdsAngelMembership', 'Basic With Ads Angel Membership'),
    BASIC_NO_ADS: t('basicAngelMembership', 'Basic Angel Membership'),
  }

  return (isGuildPlan(giverGuildPlan) ? COLOR_KEYED[giverGuildPlan] : MEMBERSHIP_KEYED[giverGuildPlan]) ?? DEFAULT_LABEL
}

function isGuildPlan(str: string | undefined | null): str is GuildPlan {
  return !!str && ['BRONZE', 'SILVER', 'GOLD'].includes(str)
}

/**
 * For gift received (via giftedMembership query)
 */
export function formatGiftReceivedMembershipTierName(t: TranslateFunction, tier?: string) {
  const MEMBERSHIP_KEYED: Record<string, string> = {
    basic: t('basicWithAds', 'Basic With Ads'),
    'basic-no-ads': t('basic', 'Basic'),
    BASIC: t('basicWithAds', 'Basic With Ads'),
    BASIC_NO_ADS: t('basic', 'Basic'),
  }

  return tier ? MEMBERSHIP_KEYED[tier] ?? tier : t('membership', 'Membership')
}

export function GiftGivenGuildShield({
  giverGuildPlan,
  ...rest
}: { giverGuildPlan: MembershipTier | GuildPlan | undefined | null } & BaseIconProps) {
  const isBronze = giverGuildPlan
    ? isGuildPlan(giverGuildPlan)
      ? ['GOLD', 'SILVER'].includes(giverGuildPlan)
      : ['BASIC_NO_ADS', 'PREMIUM', 'basic-no-ads', 'premium'].includes(giverGuildPlan)
    : false

  return giverGuildPlan && isBronze ? <GuildShield {...rest} /> : <GuildSilverIcon {...rest} />
}

export function GiftReceivedGuildShield({ tier, ...rest }: { tier?: string } & BaseIconProps) {
  return tier === 'BASIC_NO_ADS' || tier === 'basic-no-ads' ? <GuildShield {...rest} /> : <GuildSilverIcon {...rest} />
}
